/**
 * User Dashboard View
 * Displays user-specific metrics and activity
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  Zap,
  Clock,
  TrendingUp,
  Activity,
  Key,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface MetricCard {
  label: string;
  value: string | number;
  change?: number;
  icon: any;
  color: string;
}

export function UserDashboardView() {
  const { addLogEntry } = useDashboardStore();
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<MetricCard[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [usageData, setUsageData] = useState<any[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Simulate loading user metrics
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMetrics([
        {
          label: "API Calls",
          value: "12,458",
          change: 12.5,
          icon: Zap,
          color: "text-blue-500",
        },
        {
          label: "Active Keys",
          value: 3,
          icon: Key,
          color: "text-green-500",
        },
        {
          label: "This Month",
          value: "$42.50",
          change: 8.2,
          icon: DollarSign,
          color: "text-purple-500",
        },
        {
          label: "Uptime",
          value: "99.9%",
          icon: Activity,
          color: "text-emerald-500",
        },
      ]);

      setRecentActivity([
        { id: 1, action: "API Key Created", time: "2 minutes ago", status: "success" },
        { id: 2, action: "Query Completed", time: "15 minutes ago", status: "success" },
        { id: 3, action: "Key Revoked", time: "1 hour ago", status: "warning" },
        { id: 4, action: "Authentication", time: "2 hours ago", status: "success" },
      ]);

      // Generate usage data for last 7 days
      const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      setUsageData(days.map((day, i) => ({
        day,
        calls: Math.floor(Math.random() * 500) + 1000,
        cost: ((Math.random() * 2) + 4).toFixed(2),
      })));

    } catch (error) {
      console.error("Failed to load dashboard:", error);
      addLogEntry({
        category: "error",
        action: "Dashboard Load",
        details: "Failed to load dashboard data",
        severity: "error",
        source: "user-dashboard",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Activity className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Welcome back!</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Here's an overview of your usage</p>
        </div>
        <button className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer">
          <Calendar className="h-4 w-4" />
          Last 7 days
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, i) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6"
          >
            <div className="flex items-center justify-between">
              <metric.icon className={cn("h-5 w-5", metric.color)} />
              {metric.change !== undefined && (
                <span className={cn(
                  "flex items-center gap-1 text-xs font-medium",
                  metric.change >= 0 ? "text-green-500" : "text-red-500"
                )}>
                  {metric.change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {Math.abs(metric.change)}%
                </span>
              )}
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{metric.label}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Usage Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">API Usage</h3>
            <div className="flex gap-2">
              <button className="rounded-md bg-[hsl(var(--muted))] px-3 py-1 text-xs font-medium transition-colors cursor-pointer">Calls</button>
              <button className="rounded-md px-3 py-1 text-xs font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">Cost</button>
            </div>
          </div>
          <div className="flex h-48 items-end gap-4 px-4">
            {usageData.map((data, i) => (
              <motion.div
                key={data.day}
                initial={{ height: 0 }}
                animate={{ height: (data.calls / 1500) * 100 + "%" }}
                transition={{ delay: 0.4 + i * 0.05 }}
                className="flex-1 flex flex-col items-center gap-2"
              >
                <div className="w-full rounded-t-lg bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/.8)] transition-colors cursor-pointer" style={{ height: `${(data.calls / 1500) * 100}%` }} />
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{data.day}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6"
        >
          <h3 className="mb-4 font-semibold">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, i) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex items-start gap-3"
              >
                <div className={cn(
                  "mt-1 flex h-2 w-2 shrink-0 rounded-full",
                  activity.status === "success" ? "bg-green-500" : "bg-yellow-500"
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{activity.action}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{activity.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <button className="mt-4 w-full rounded-lg border border-[hsl(var(--border))] py-2 text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
            View All Activity
          </button>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6"
      >
        <h3 className="mb-4 font-semibold">Quick Actions</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <button className="flex items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] py-3 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
            <Key className="h-4 w-4" />
            Create API Key
          </button>
          <button className="flex items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] py-3 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
            <BarChart3 className="h-4 w-4" />
            View Analytics
          </button>
          <button className="flex items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] py-3 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
            <Clock className="h-4 w-4" />
            View Logs
          </button>
        </div>
      </motion.div>
    </div>
  );
}
